import { useEffect, useState } from "react";
import {
  useUploadBaseImageMutation,
  useRemoveBaseImageMutation,
} from "../useQuery";

export const useDesignPrincipleImage = ({
  testCaseId,
  testStepId = null,
  principleLevel = "Test_case",
  fileName,
  referenceStepId: initialReferenceStepId,
  refetch,
  notify,
  successMessage = "Image uploaded successfully",
}) => {
  const { mutate: uploadImage } = useUploadBaseImageMutation();
  const { mutate: removeImage } = useRemoveBaseImageMutation();

  const [pendingImage, setPendingImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [existingBaseImage, setExistingBaseImage] = useState(null);
  const [pendingReferenceStepId, setPendingReferenceStepId] = useState(null);

  const isStep = principleLevel === "Test_step";
  const levelParams = isStep ? { principleLevel, testStepId } : {};

  useEffect(() => {
    setExistingBaseImage(fileName ? { name: fileName } : null);
    setPendingReferenceStepId(fileName ? null : (initialReferenceStepId ?? null));
    setPendingImage(null);
  }, [fileName, testStepId, initialReferenceStepId]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPendingImage(file);
  };

  const handleConfirmUpload = () => {
    if (!pendingImage || !testCaseId || (isStep && !testStepId)) {
      notify(
        isStep ? "Missing image, test case ID, or step" : "Missing image or test case ID",
        "error",
      );
      return;
    }
    setIsUploading(true);
    uploadImage(
      { testCaseId, image: pendingImage, ...levelParams },
      {
        onSuccess: () => {
          setExistingBaseImage({ name: pendingImage.name });
          setPendingReferenceStepId(null);
          setPendingImage(null);
          setIsUploading(false);
          notify(successMessage, "success");
          refetch?.();
        },
        onError: () => {
          setIsUploading(false);
          notify(isStep ? "Failed to upload step image" : "Failed to upload image", "error");
        },
      },
    );
  };

  const handleRemoveImage = () => {
    setPendingImage(null);
    setExistingBaseImage(null);
    setPendingReferenceStepId(null);
    removeImage({ testCaseId, ...levelParams }, { onSuccess: () => refetch?.() });
  };

  const handleSelectStepImage = (sourceStepId) => {
    if (!sourceStepId || !testCaseId || !testStepId) {
      notify("Missing step or test case ID", "error");
      return;
    }
    setIsUploading(true);
    uploadImage(
      { testCaseId, testStepId, principleLevel: "Test_step", sourceTestStepId: sourceStepId },
      {
        onSuccess: (res) => {
          setIsUploading(false);
          if (res?.status === "pending") {
            setExistingBaseImage(null);
            setPendingReferenceStepId(sourceStepId);
            notify(res?.message || "No screenshot yet — will apply automatically once available", "info");
            return;
          }
          setExistingBaseImage(res?.fileName ? { name: res.fileName } : null);
          setPendingReferenceStepId(null);
          notify("Step image set successfully", "success");
          refetch?.();
        },
        onError: () => {
          setIsUploading(false);
          notify("Failed to set step image", "error");
        },
      },
    );
  };

  return {
    pendingImage,
    isUploading,
    existingBaseImage,
    pendingReferenceStepId,
    handleImageUpload,
    handleConfirmUpload,
    handleRemoveImage,
    handleSelectStepImage,
  };
};

export default useDesignPrincipleImage;